import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Check, ShieldCheck } from "lucide-react";
import { SiteFooter, SiteHeader } from "@/components/layout/SiteChrome";
import { Button } from "@/components/ui/button";

const BASE_URL = "https://adkiro.com";
const PAGE_URL = BASE_URL + "/case-studies/local-lead-generation-meta-ads";
const TITLE = "Case Study: 513 Property Leads at €3.10 CPL | Adkiro";
const DESCRIPTION =
  "17 Meta lead campaigns for a Sofia property business: 513 leads from €1,592.41 spend at a €3.10 blended cost per lead, every campaign between €1.00 and €4.61.";

// Real figures from the Valore ad account, read directly from Meta Ads
// Manager. Valore (valore.life) is our own property business in Sofia — this
// is first-party work, not a client engagement, and the page says so.
//
// Excluded on purpose: one campaign with zero spend and no data, and one
// campaign optimised for link clicks rather than leads (counting its 195
// clicks as "leads" would inflate the totals).
const CAMPAIGN_RESULTS = [
  { campaign: "Loz", leads: 94, cpl: "€3.94", spend: "€370.00", impressions: "135,671" },
  { campaign: "HAPPY", leads: 68, cpl: "€2.72", spend: "€184.80", impressions: "70,989" },
  { campaign: "Banishora", leads: 66, cpl: "€3.41", spend: "€224.99", impressions: "97,525" },
  { campaign: "LOZ", leads: 61, cpl: "€2.58", spend: "€157.52", impressions: "51,502" },
  { campaign: "VIDIN_SOFIA", leads: 41, cpl: "€1.71", spend: "€70.21", impressions: "20,950" },
  { campaign: "DRAGALAKA", leads: 32, cpl: "€3.78", spend: "€120.83", impressions: "56,244" },
  { campaign: "VIDIN", leads: 31, cpl: "€4.61", spend: "€143.05", impressions: "47,930" },
  { campaign: "EPK", leads: 27, cpl: "€1.57", spend: "€42.29", impressions: "19,443" },
  { campaign: "Bakston", leads: 27, cpl: "€3.67", spend: "€99.05", impressions: "27,788" },
  { campaign: "Dragalaka", leads: 17, cpl: "€4.03", spend: "€68.48", impressions: "19,869" },
  { campaign: "ORHAN", leads: 9, cpl: "€2.42", spend: "€21.77", impressions: "8,634" },
  { campaign: "Grafa", leads: 9, cpl: "€2.28", spend: "€20.49", impressions: "5,707" },
  { campaign: "Serviz", leads: 8, cpl: "€1.32", spend: "€10.57", impressions: "2,911" },
  { campaign: "Vazov_Baby", leads: 8, cpl: "€4.06", spend: "€32.51", impressions: "8,005" },
  { campaign: "TOP", leads: 6, cpl: "€1.00", spend: "€6.00", impressions: "1,934" },
  { campaign: "Drujba", leads: 5, cpl: "€2.98", spend: "€14.89", impressions: "3,230" },
  { campaign: "OFIS", leads: 4, cpl: "€1.24", spend: "€4.96", impressions: "1,489" },
];

export const Route = createFileRoute("/case-studies/local-lead-generation-meta-ads")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: PAGE_URL },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "@id": PAGE_URL + "#article",
              headline: "513 Property Leads at a €3.10 Blended CPL",
              description: DESCRIPTION,
              url: PAGE_URL,
              datePublished: "2026-08-21",
              dateModified: "2026-08-21",
              author: {
                "@type": "Person",
                "@id": BASE_URL + "/about#kristian-kinchev",
                name: "Kristian Kinchev",
              },
              publisher: { "@id": BASE_URL + "/#organization" },
              mainEntityOfPage: { "@id": PAGE_URL + "#page" },
            },
            {
              "@type": "WebPage",
              "@id": PAGE_URL + "#page",
              url: PAGE_URL,
              name: TITLE,
              description: DESCRIPTION,
              isPartOf: { "@id": BASE_URL + "/#website" },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: BASE_URL + "/",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Case Studies",
                  item: BASE_URL + "/case-studies",
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: "Local Lead Generation",
                  item: PAGE_URL,
                },
              ],
            },
          ],
        }),
      },
    ],
  }),
  component: LocalLeadGenerationCaseStudy,
});

function LocalLeadGenerationCaseStudy() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader tone="ink" />

      <main>
        <article>
          <header data-tone="ink" className="tone-ink relative overflow-hidden">
            <div className="relative mx-auto max-w-6xl px-5 pb-20 pt-16 sm:pt-24">
              <div className="max-w-4xl">
                {/* The site header no longer carries this link, so the way back is here. */}
                <nav aria-label="Breadcrumb" className="mb-8 text-xs text-muted-foreground">
                  <Link to="/" className="hover:text-foreground">
                    Home
                  </Link>{" "}
                  <span aria-hidden="true">/</span>{" "}
                  <Link to="/case-studies" className="hover:text-foreground">
                    Case studies
                  </Link>
                </nav>
                <p className="text-xs uppercase tracking-[0.25em] text-primary">
                  First-party results · Property lead generation · Sofia
                </p>
                <h1 className="mt-5 text-4xl font-bold leading-[1.08] sm:text-6xl">
                  513 property leads at a <span className="text-gradient">€3.10 blended CPL</span>
                </h1>
                <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
                  Seventeen Meta lead campaigns run for{" "}
                  <a
                    href="https://valore.life"
                    className="text-foreground underline underline-offset-4 hover:text-primary"
                  >
                    Valore
                  </a>
                  , our own property business in Sofia: 513 leads from €1,592.41 in recorded spend,
                  across 579,821 impressions. Every campaign landed between €1.00 and €4.61 per
                  lead.
                </p>
                <p className="mt-5 text-sm text-muted-foreground">
                  Prepared by Kristian Kinchev · Adkiro · August 21, 2026
                </p>
              </div>
            </div>
          </header>

          <section aria-label="Case study results" className="mx-auto max-w-6xl px-5 py-12">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {[
                ["513", "Recorded Meta leads"],
                ["€3.10", "Blended cost per lead"],
                ["€1,592", "Recorded spend"],
                ["579,821", "Impressions"],
              ].map(([value, label]) => (
                <div key={label} className="glass rounded-2xl p-6">
                  <p className="font-display text-3xl font-bold text-gradient sm:text-4xl">
                    {value}
                  </p>
                  <p className="mt-2 text-xs text-muted-foreground">{label}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mx-auto grid max-w-6xl gap-8 px-5 py-16 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-primary">The evidence</p>
              <h2 className="mt-3 text-3xl font-bold sm:text-4xl">
                What the source report confirms
              </h2>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Every figure below is read directly from the Valore Meta Ads account. Valore is our
                own property business, so these are first-party results rather than client work — we
                are showing our own book, not someone else's. Campaign names are the real internal
                names, mostly Sofia districts. Blended CPL is total recorded spend divided by total
                leads.
              </p>
              <div className="mt-6 rounded-2xl border border-primary/20 bg-primary/5 p-5">
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Calculation: €1,592.41 ÷ 513 leads = €3.104, rounded to €3.10 per lead.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Two campaigns are excluded: one with zero spend and no recorded data, and one
                  optimised for link clicks rather than leads — counting its 195 clicks as leads
                  would overstate the result.
                </p>
              </div>
            </div>

            <div className="overflow-hidden rounded-3xl border border-border">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[620px] text-left text-sm">
                  <thead className="bg-surface text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-5 py-4 font-medium">Campaign</th>
                      <th className="px-5 py-4 font-medium">Leads</th>
                      <th className="px-5 py-4 font-medium">CPL</th>
                      <th className="px-5 py-4 font-medium">Spend</th>
                      <th className="px-5 py-4 font-medium">Impressions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CAMPAIGN_RESULTS.map((row) => (
                      <tr key={row.campaign} className="border-t border-border/60">
                        <td className="px-5 py-4 font-medium">{row.campaign}</td>
                        <td className="px-5 py-4 text-muted-foreground">{row.leads}</td>
                        <td className="px-5 py-4 text-muted-foreground">{row.cpl}</td>
                        <td className="px-5 py-4 text-muted-foreground">{row.spend}</td>
                        <td className="px-5 py-4 text-muted-foreground">{row.impressions}</td>
                      </tr>
                    ))}
                    <tr className="border-t border-primary/30 bg-primary/5 font-semibold">
                      <td className="px-5 py-4">Total / blended</td>
                      <td className="px-5 py-4">513</td>
                      <td className="px-5 py-4">€3.10</td>
                      <td className="px-5 py-4">€1,592.41</td>
                      <td className="px-5 py-4">579,821</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section className="mx-auto max-w-6xl px-5 py-16">
            <div className="grid gap-8 lg:grid-cols-2">
              <div className="glass rounded-3xl p-7 sm:p-9">
                <h2 className="text-2xl font-semibold">What this snapshot demonstrates</h2>
                <ul className="mt-6 space-y-4">
                  {[
                    "Lead volume was spread across 17 separate campaigns, not one lucky winner.",
                    "Every one of the 17 campaigns landed between €1.00 and €4.61 per lead.",
                    "The most efficient campaign produced six leads at €1.00 each.",
                    "The largest single campaign produced 94 leads at €3.94 each.",
                  ].map((item) => (
                    <li key={item} className="flex gap-3 text-sm text-muted-foreground">
                      <Check className="mt-0.5 size-4 shrink-0 text-primary" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="glass rounded-3xl p-7 sm:p-9">
                <h2 className="text-2xl font-semibold">Important context</h2>
                <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
                  This is a reporting snapshot, not a promise that every account will achieve the
                  same cost per lead. Costs vary by offer, market, geography, creative, seasonality,
                  sales process and measurement setup. Final lead quality and closed revenue should
                  also be evaluated outside Ads Manager.
                </p>
              </div>
            </div>
          </section>

          <section className="mx-auto max-w-6xl px-5 pb-24 pt-12">
            <div className="glass relative overflow-hidden rounded-3xl px-6 py-14 text-center">
              <div className="pointer-events-none absolute inset-0 halo" />
              <div className="relative">
                <ShieldCheck className="mx-auto size-7 text-primary" />
                <h2 className="mt-5 text-3xl font-bold sm:text-4xl">
                  Want a clearer view of your Meta Ads account?
                </h2>
                <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                  A complimentary fit review comes first. Suitable accounts proceed to the $950
                  written diagnostic, prioritised 90-day roadmap and implementation handover, all by
                  email.
                </p>
                <Button variant="brand" size="xl" className="mt-8" asChild>
                  <a href="/#audit">
                    Request a fit review <ArrowRight className="size-4" />
                  </a>
                </Button>
              </div>
            </div>
          </section>
        </article>
      </main>

      <SiteFooter />
    </div>
  );
}
